import * as React from "react";

import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "@shared-ui/viewport-context";

import { UitkPrimaryButton, UitkSecondaryButton, UitkButtonSize } from "uitk-react-button";
import { UitkSpacing, UitkSpacingProps } from "uitk-react-spacing";
import { UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkLayoutFlexProps } from "uitk-react-layout-flex";
import { UitkLayoutConditionalGridTrack, UitkLayoutConditionalGridSpan } from "uitk-react-layout-grid";

import { FTNodeOptions, ExtendedFTNode, ComponentProps } from "typings/flexFramework/FlexComponent";
import { EditorialFlexModule, FlexModuleResult } from "typings/microserviceModels/content-flex-module";

import { ScrimCard } from "components/shared/ScrimCard/ScrimCard";
import { UitkScrimType } from "uitk-react-scrim";
import { FAILED_MODEL_RENDER } from "src/config/systemEvents";
import { NOOP_LOGGER as logger } from "bernie-logger";

import { FlexModuleModelStore } from "stores/FlexModuleModelStore";
import { observer } from "mobx-react";
import { normalizeOptionsData } from "src/components/flexFramework/FlexTemplateNode";
import { withStores } from "stores";
import { SpacingSize } from "src/components/utility/FlexAttributesUtil";
import { EGClickStreamTracker } from "src/components/utility/analytics/EGClickStream/EGClickStreamTracker";

export interface EditorialBannerFTNodeOptions extends FTNodeOptions {}

export interface EditorialBannerFTNode extends ExtendedFTNode {
  options: EditorialBannerFTNodeOptions;
}

export interface EditorialBannerProps extends ComponentProps {
  flexTemplateNode: EditorialBannerFTNode;
  flexModuleModelStore: FlexModuleModelStore;
  isHero?: boolean;
}

export const EditorialBanner = withStores("flexModuleModelStore")(
  observer((props: EditorialBannerProps) => {
    try {
      const { useRef, useEffect, useState } = React;
      const { flexTemplateNode, flexModuleModelStore, isHero } = props;
      const { id } = flexTemplateNode;
      const model = flexModuleModelStore.getModel(id) as FlexModuleResult;
      const options = normalizeOptionsData(flexTemplateNode, model) as EditorialBannerFTNodeOptions;
      const { fmId } = options;
      const scrimRef = useRef<HTMLElement>(null);
      const [isSmallScrim, setSmallScrim] = useState(false);
      const smallLimitWidth = 800;

      useEffect(() => {
        if (!isSmallScrim && scrimRef && scrimRef.current && scrimRef.current.offsetWidth < smallLimitWidth) {
          setSmallScrim(true);
        } else if (isSmallScrim) {
          setSmallScrim(false);
        }
      }, []);

      /* istanbul ignore next*/
      if (!model) {
        return null;
      }

      // @ts-ignore
      const [{ media, text }] = model.items;
      const { padding, textTheme = "inverse" } = model as EditorialFlexModule;
      /**
       * This is a temporary work around due to limitations of the call-to-action model that will be addressed in the future.
       * Parsing data from contributed HTML content should not be considered an accepted approach to be repeated elsewhere
       */
      const htmlText = text as string;

      const [, title] = /<h3>(.*?)<\/h3>/g.exec(htmlText) || [];
      const [, subtitle] = /<p class="subtitle">(.*?)<\/p>/g.exec(htmlText) || [];
      const [, ctaLabel] = /<p><button>(.*?)<\/button><\/p>/g.exec(htmlText) || [];
      const [, subtitleTerm] = /<p class="subtitle-term">(.*?)<\/p>/g.exec(htmlText) || [];

      // Media content
      const [desktopMedia, mobileMedia] = media;
      const { mediaUrl: desktopMediaURL } = desktopMedia;
      const { mediaUrl: mobileMediaURL } = mobileMedia;
      const { clickUrl } = desktopMedia;

      // Component display properties
      const hasNoPadding = padding === SpacingSize.NONE;
      const className = "EditorialBanner";
      const spacing: UitkSpacingProps = { margin: { inline: "three", blockstart: hasNoPadding ? "unset" : "three" } };

      const justifyContent: UitkLayoutFlexProps["justifyContent"] = "center";

      const mainGridColumns: UitkLayoutConditionalGridTrack = {
        small: 1,
        medium: isHero ? 1 : 2,
        large: isHero ? 3 : 2,
      };
      const headingCols: UitkLayoutConditionalGridSpan = { small: 1, medium: isSmallScrim || isHero ? 1 : 2 };

      const CtaButton = EGClickStreamTracker(isHero ? UitkPrimaryButton : UitkSecondaryButton);
      const largeFontSizeTitle = isHero ? 800 : 700;
      const smallFontSizeTitle = isHero ? 700 : 600;
      const fontSizeSubtitle = isHero || isSmallScrim ? 400 : 500;
      const fontSizeTerm = 200;
      const buttonCtaSize = isHero ? UitkButtonSize.LARGE : UitkButtonSize.MEDIUM;
      const interiorSpacing: UitkSpacingProps = {
        padding: { inline: isHero && !isSmallScrim ? "twelve" : "six" },
      };
      const moduleName = isHero ? "EditorialHeroBanner" : className;

      const scrimCardProps = {
        id: `editorial-banner-${fmId}`,
        className,
        titleSlot: (
          <UitkHeading tag="h2">
            <UitkText size={isSmallScrim ? smallFontSizeTitle : largeFontSizeTitle} theme={textTheme}>
              {title}
            </UitkText>
          </UitkHeading>
        ),
        subtitleSlot: (
          <UitkText size={fontSizeSubtitle} theme={textTheme}>
            {subtitle}
          </UitkText>
        ),
        footerSlot: (
          <UitkSpacing margin={{ blockend: "one" }}>
            <div>
              <UitkSpacing margin={{ block: "three" }}>
                <CtaButton
                  moduleName={moduleName}
                  linkName="CTA button clicked"
                  rfrr={`home.${moduleName}`}
                  size={buttonCtaSize}
                  tag="a"
                  href={clickUrl}
                >
                  {ctaLabel}
                </CtaButton>
              </UitkSpacing>
              {subtitleTerm && (
                <UitkText data-testid="editorial-banner-term" size={fontSizeTerm} theme={textTheme}>
                  {subtitleTerm}
                </UitkText>
              )}
            </div>
          </UitkSpacing>
        ),
        spacing,
        mainGridColumns,
        padding,
        headingCols,
        interiorSpacing,
      };

      const largeAspectRatio =
        isHero || isSmallScrim ? UitkFigureAspectRatioType.R21_9 : UitkFigureAspectRatioType.R4_1;

      return (
        <Viewport>
          <ViewSmall>
            <ScrimCard
              {...scrimCardProps}
              aspectRatio={isHero ? UitkFigureAspectRatioType.R3_4 : UitkFigureAspectRatioType.R1_1}
              backgroundImageURL={mobileMediaURL}
              scrimType={UitkScrimType.BOTTOM}
              justifyContent={isSmallScrim ? "end" : justifyContent}
              domRef={scrimRef}
              lazyLoading="lazy"
            />
          </ViewSmall>
          <ViewMedium>
            <ScrimCard
              {...scrimCardProps}
              aspectRatio={UitkFigureAspectRatioType.R21_9}
              backgroundImageURL={desktopMediaURL}
              scrimType={UitkScrimType.OVERLAY}
              domRef={scrimRef}
              justifyContent={justifyContent}
              verticallyCenter
              lazyLoading="lazy"
            />
          </ViewMedium>
          <ViewLarge>
            <ScrimCard
              {...scrimCardProps}
              aspectRatio={largeAspectRatio}
              backgroundImageURL={desktopMediaURL}
              scrimType={UitkScrimType.OVERLAY}
              domRef={scrimRef}
              justifyContent={justifyContent}
              verticallyCenter
              lazyLoading="lazy"
            />
          </ViewLarge>
        </Viewport>
      );
    } catch (error) {
      logger.logEvent(FAILED_MODEL_RENDER, "Unexpected error occurred while rendering EditorialBanner module.", error);

      return null;
    }
  })
);

export default EditorialBanner;
